import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DateUtil } from "../../common/utils/date.util.js";
import { useMaintenanceCommand } from "./hooks/useMaintenanceCommand.js";
import { useMaintenanceQuery } from "./hooks/useMaintenanceQuery.js";
import { useCountMaintenancesQuery } from "./hooks/useCountMaintenancesQuery.js";
import TechnicalDataPDF from "./components/TechnicalDataPDF.jsx";
import FormMaintenance from "./components/FormMaintenance.jsx";
import CountMaintenance from "./components/CountMaintenance.jsx";
import Textfield from "../../common/components/Textfiled.jsx";
import Select from "../../common/components/Select.jsx";
import Table from "../../common/components/Table.jsx";
import Pagination from "../../common/components/Pagination";
import IconButton from "../../common/components/IconButton";
import { ROUTES } from "../../core/routes/routes.js";
import {
  BsCheckCircleFill,
  BsXCircleFill,
  BsFileSpreadsheet,
  BsClipboardPlus,
  BsTrash,
  BsPencilSquare,
} from "react-icons/bs";

function ListMaintenance() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    startDate: DateUtil.getStartOfYear(),
    endDate: DateUtil.now(),
    type: "preventivo",
    done: false,
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const {
    maintenances,
    currentPage,
    onChangePage,
    totalPages,
    loading,
    refresh,
  } = useMaintenanceQuery(filters);

  const countFilters = useMemo(
    () => ({ startDate: filters.startDate, endDate: filters.endDate }),
    [filters.startDate, filters.endDate]
  );
  const { countMaintenances, refreshCount } = useCountMaintenancesQuery(countFilters);
  const {
    exportToExcel,
    updateDoneStatus,
    deleteMaintenance,
    updateMaintenance,
    maintenance,
    handleChange,
    setDefaultValues,
    resetForm,
    errors: { errors },
    loading: commandLoading,
  } = useMaintenanceCommand(() => {
    refresh();
    refreshCount();
  });

  const handleEdit = (item) => {
    setDefaultValues({
      ...item,
      date: item.date ? DateUtil.format(item.date, "YYYY-MM-DD") : "",
      nextPreventiveMaintenance: item.nextPreventiveMaintenance
        ? DateUtil.format(item.nextPreventiveMaintenance, "YYYY-MM-DD")
        : null,
    });
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    resetForm();
  };

  const handleUpdate = async () => {
    const success = await updateMaintenance();
    if (success) {
      handleCloseEdit();
    }
  };

  const countFilterLabel = useMemo(() => {
    if (filters.startDate && filters.endDate) {
      return `Desde ${DateUtil.format(filters.startDate)} hasta ${DateUtil.format(filters.endDate)}`;
    }
    if (filters.startDate) return `Desde ${DateUtil.format(filters.startDate)}`;
    if (filters.endDate) return `Hasta ${DateUtil.format(filters.endDate)}`;
    return "Todos los registros (sin filtro de fecha)";
  }, [filters.startDate, filters.endDate]);

  const columns = [
    { header: "Placa", accessor: "plate" },
    { header: "Tipo", accessor: "type" },
    {
      header: "Fecha",
      accessor: "date",
      render: (value) => DateUtil.format(value),
    },
    {
      header: "Kilometraje",
      accessor: "mileage",
      render: (value) => value.toLocaleString(),
    },
    {
      header: "Observaciones",
      accessor: "observations",
      render: (value) => value || "—",
      width: "200px",
    },
    {
      header: "Pro. Manten.",
      accessor: "nextPreventiveMaintenance",
      render: (value) => (value ? DateUtil.format(value) : "—"),
    },
    {
      header: "Cumplim.",
      accessor: "compliance",
    },
    {
      header: "Realizado",
      render: (_, item) => (item.done ? "Realizado" : "No realizado"),
    },
    { header: "Proveedor", accessor: "provider" },
    {
      header: "Valor Factura",
      accessor: "valueInvoice",
      render: (value) => `$${value.toLocaleString()}`,
    },
    {
      header: "Acciones",
      accessor: "actions",
      render: (_, item) => (
        <div className="d-flex align-items-center gap-2">
          <TechnicalDataPDF maintenance={item} variant="icon" />

          <IconButton tooltipText="Editar" onClick={() => handleEdit(item)}>
            <BsPencilSquare size={20} />
          </IconButton>

          {!item.done ? (
            <IconButton
              tooltipText="Marcar como realizado"
              onClick={() =>
                updateDoneStatus({
                  id: item.id,
                  done: true,
                  plate: item.plate,
                })
              }
            >
              <BsCheckCircleFill size={25} />
            </IconButton>
          ) : (
            <IconButton
              tooltipText="Desmarcar como realizado"
              className="text-warning"
              onClick={() =>
                updateDoneStatus({
                  id: item.id,
                  done: false,
                  plate: item.plate,
                })
              }
            >
              <BsXCircleFill size={25} />
            </IconButton>
          )}

          <IconButton
            tooltipText="Eliminar"
            onClick={() =>
              deleteMaintenance({
                id: item.id,
                plate: item.plate,
              })
            }
            className="text-danger"
          >
            <BsTrash size={20} />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <main className="container-fluid p-1">
      <section className="d-flex flex-column shadow-sm w-100 mb-0" style={{ overflow: "visible" }}>
        <CountMaintenance
          plates={countMaintenances.countPlates}
          schedulePreventiveMaintenance={
            countMaintenances.countSchedulePreventiveMaintenance
          }
          correctiveMaintenance={countMaintenances.countCorrectiveMaintenance}
          preventiveMaintenancePerformed={
            countMaintenances.countPreventiveMaintenancePerformed
          }
          filterLabel={countFilterLabel}
        />
      </section>

      <header className="d-flex justify-content-between align-items-center mb-0 px-3 py-0 border-bottom">
        <h1 className="h2 m-0 fw-bold">Lista de mantenimientos</h1>
        <div className="d-flex gap-2">
          <IconButton onClick={() => navigate(ROUTES.REGISTER_MAINTENANCE)} tooltipText="Registrar">
            <BsClipboardPlus size={24} />
          </IconButton>
          <IconButton
            onClick={() =>
              exportToExcel((({ type, ...rest }) => rest)(filters))
            }
            tooltipText="Exportar"
          >
            <BsFileSpreadsheet size={24} />
          </IconButton>
        </div>
      </header>

      <section className="shadow w-100 d-flex flex-column">
        <div className="d-flex gap-3 justify-content-center flex-wrap flex-md-nowrap justtify-content-md-end mb-0">
          <div>
            <Select
              label="Realizado"
              value={filters.done}
              onChange={({ value }) =>
                setFilters((prev) => ({ ...prev, done: value }))
              }
              options={[
                { label: "No", value: false },
                { label: "Sí", value: true },
              ]}
              noDefaultOption
            />
          </div>
          <div className="d-flex gap-1">
            <Textfield
              label="Fecha inicio"
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={({ value }) =>
                setFilters((prev) => ({ ...prev, startDate: value }))
              }
            />
            <Textfield
              label="Fecha fin"
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={({ value }) =>
                setFilters((prev) => ({ ...prev, endDate: value }))
              }
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={maintenances}
          showIndex={true}
          loading={loading}
        />

        <Pagination
          onChangePage={onChangePage}
          page={currentPage}
          totalPages={totalPages}
        />
      </section>

      {showEditModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={handleCloseEdit}
        >
          <div
            className="bg-white rounded shadow p-4"
            style={{
              maxWidth: 900,
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-3">Editar mantenimiento</h3>
            <FormMaintenance
              maintenance={maintenance}
              onChange={handleChange}
              onSubmit={handleUpdate}
              onCancel={handleCloseEdit}
              errors={errors}
              submitLabel="Guardar"
              loading={commandLoading}
            />
          </div>
        </div>
      )}
    </main>
  );
}

export default ListMaintenance;
